/**
 * React Query hooks for managing recommendation state
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type CustomerHistoryResponse,
  type FeedbackRequest,
  type FeedbackResponse,
  getCustomerHistory,
  getPersonalizedRecommendations,
  getRecommendationsByEmail,
  type RecommendationByEmailResponse,
  submitFeedback,
} from '../services/api';

// An all-digit identifier is treated as a Shopify customer ID; anything else
// (real emails, plus demo logins like "heather"/"heather-kids") is treated as
// an email, since those demo accounts have no "@".
const SHOPIFY_CUSTOMER_ID_PATTERN = /^\d+$/;

/**
 * Hook for getting recommendations from a single identifier that may be either
 * an email address or a Shopify customer ID.
 *
 * Routes all-digit identifiers to the by-ID endpoint and everything else to the
 * by-email endpoint. The mutation may resolve with either a full
 * RecommendationResult (cache hit) or a RecommendationQueuedResponse
 * (cache miss → queued).
 */
export const useRecommendationsByIdentifier = () => {
  return useMutation<RecommendationByEmailResponse, Error, string>({
    mutationFn: (identifier: string) => {
      const trimmed = identifier.trim();
      return SHOPIFY_CUSTOMER_ID_PATTERN.test(trimmed)
        ? getPersonalizedRecommendations(trimmed)
        : getRecommendationsByEmail(trimmed);
    },
  });
};

/**
 * Hook for fetching personalized recommendations by shopify_customer_id.
 * Used on the /recommendations/:shopifyCustomerId route.
 */
export const usePersonalizedRecommendations = (shopifyCustomerId: string) => {
  return useQuery<RecommendationByEmailResponse, Error>({
    queryKey: ['personalizedRecommendations', shopifyCustomerId],
    queryFn: () => getPersonalizedRecommendations(shopifyCustomerId),
    enabled: !!shopifyCustomerId,
    retry: 1,
  });
};

/**
 * Hook for fetching customer purchase/browse history by shopify_customer_id.
 *
 * Fires once shopifyCustomerId is known (i.e. recommendations have loaded).
 * Callers that have nothing to render it with should pass `enabled: false`:
 * the history is only read by the internal tabs.
 */
export const useCustomerHistory = (
  shopifyCustomerId: string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery<CustomerHistoryResponse, Error>({
    queryKey: ['customerHistory', shopifyCustomerId],
    queryFn: () => getCustomerHistory(shopifyCustomerId),
    enabled: enabled && !!shopifyCustomerId,
    retry: 1,
  });
};

/**
 * Hook for submitting user feedback
 */
export const useFeedbackSubmit = () => {
  return useMutation<FeedbackResponse, Error, FeedbackRequest>({
    mutationFn: submitFeedback,
  });
};
